import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useOrg } from '../app/OrgProvider';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink, useGo } from '../components/Nav';
import { UsersIcon } from '../components/icons';
import { Banner, Button, ErrorText, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { useTheme } from '../lib/theme';
import { retryAfterSec, useCooldown } from '../lib/useCooldown';
import { logoFor } from '../queries/branding';
import type { InviteDetails, ShareBranding } from '../lib/types';
import { qk } from '../queries/keys';
import {
  acceptInvite,
  exchangeInviteCode,
  fetchInvite,
  resendInvite,
  roleDescription,
  roleLabel,
} from '../queries/orgs';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../lib/firebase';

/**
 * Accepting an invitation. Rendered outside the app shell: the person opening
 * this may not have an account yet, and a nav bar full of things they cannot
 * reach would be noise.
 *
 * Joining a team is a bigger step for someone who already uses Slate than for
 * someone who signed up from the invitation, so the page says what changes
 * before the button is pressed: which team, who asked, what the team will
 * see, who pays. A person who cannot join, because they own a plan of their
 * own or are already on another team, is told that first, not after a click.
 */

/**
 * The frame every state of this page shares.
 *
 * `branding` arrives with the invitation, so from the moment the team is
 * known the page wears their mark and their colour: this is somebody's first
 * sight of the thing they are being asked to join, and it was the only page
 * in the flow still showing ours instead of theirs.
 */
function Shell({ branding, children }: { branding?: ShareBranding | null; children: ReactNode }) {
  // This page is not pinned to the team's theme (it is a Slate page about
  // joining a Slate team), so it is lit however this person keeps the app,
  // and the logo is the one drawn for that.
  const { theme } = useTheme();
  const logo = logoFor(branding?.logos, theme, 'wide');
  return (
    <div
      className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12"
      style={
        branding?.brandColor
          ? ({ '--brand': branding.brandColor, '--brand-ink': branding.brandInk ?? '#0b0d10' } as CSSProperties)
          : undefined
      }
    >
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          {logo ? (
            <img src={logo} alt={branding?.name ?? ''} className="max-h-9 w-auto max-w-[220px] object-contain" />
          ) : (
            <Wordmark />
          )}
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function failureTitle(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNotFound) return 'This invitation is no longer valid';
    if (error.code === 'has_subscription') return 'You cannot join this team: you have an active subscription';
    if (error.code === 'already_in_org') return 'You cannot join this team: you are already on a team';
    if (error.status === 409) return 'This invitation has already been used';
    if (error.status === 403) return 'This invitation cannot be accepted';
  }
  return 'Could not accept the invitation';
}

function recoverable(error: unknown): boolean {
  if (error instanceof ApiError) return !(error.isNotFound || error.status === 409 || error.status === 403);
  return true;
}

/** Why the signed-in account cannot accept, in the person's own terms. */
function Blocked({ details, onSignOut }: { details: InviteDetails; onSignOut: () => void }) {
  const { blocker, org } = details;
  const { sendVerification } = useAuth();
  const verify = useMutation({ mutationFn: sendVerification });
  if (!blocker) return null;
  if (blocker.code === 'wrong_account') {
    // The API masks the address for an account it was not sent to; naming
    // it in full would tell a stranger holding the link who was invited.
    return (
      <Banner tone="caution" title={`This invitation was sent to ${details.emailHint ?? 'another address'}`}>
        Sign in with that address to join {org.name}.{' '}
        <button type="button" onClick={onSignOut} className="underline hover:text-chalk">
          Sign out
        </button>
      </Banner>
    );
  }
  if (blocker.code === 'unverified_email') {
    // Right address, but nothing has shown it is theirs: a password signup
    // can put any address on an account. The link in the invitation email
    // is proof enough on its own; so is the confirmation email.
    return (
      <Banner tone="caution" title="Confirm your email address to join">
        This invitation was sent to {details.email}, and this account has that address, but it has not been
        confirmed yet. Open the invitation from your email, or confirm the address and come back.
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button size="sm" busy={verify.isPending} disabled={verify.isSuccess} onClick={() => verify.mutate()}>
            {verify.isSuccess ? 'Sent' : 'Email me a confirmation link'}
          </Button>
          {verify.isSuccess ? <span className="text-caption text-go">Check your inbox.</span> : null}
          {verify.isError ? <ErrorText>{errorMessage(verify.error)}</ErrorText> : null}
        </div>
      </Banner>
    );
  }
  if (blocker.code === 'has_subscription') {
    return (
      <Banner tone="danger" title="You cannot join this team: you have an active subscription">
        You own {blocker.orgName}, and its plan is running. A person is on one team at a time, so cancel that
        subscription, or hand the team to someone else and leave it, before joining {org.name}.
      </Banner>
    );
  }
  return (
    <Banner tone="danger" title="You cannot join this team: you are already on a team">
      You are a member of {blocker.orgName}. A person is on one team at a time, so leave it, or ask an admin there
      to remove you, before joining {org.name}.
    </Banner>
  );
}

export function InvitePage({ token }: { token: string }) {
  /** Sign in or sign up, and land back on this invitation rather than adrift. */
  const returnHere = (path: string) => `${path}?next=${encodeURIComponent(`/invite/${token}`)}`;

  /**
   * The code from the email, if this is the emailed copy of the link.
   *
   * Read once, on the first render, and taken out of the address bar as soon
   * as it has been spent: a sign-in credential has no business sitting in
   * browser history, a bookmark, or the referrer of the next page.
   */
  const [code] = useState(() => new URLSearchParams(window.location.search).get('code'));
  const [autoSignIn, setAutoSignIn] = useState<'idle' | 'working' | 'failed'>(code ? 'working' : 'idle');

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    void (async () => {
      try {
        const { customToken } = await exchangeInviteCode(token, code);
        if (cancelled) return;
        await signInWithCustomToken(auth, customToken);
        if (!cancelled) setAutoSignIn('idle');
      } catch {
        // Spent, expired, or an invitation that has moved on. The page falls
        // back to asking, and offers a fresh one.
        if (!cancelled) setAutoSignIn('failed');
      } finally {
        // Whether or not it worked, the code does not stay in the URL.
        window.history.replaceState({}, '', `/invite/${token}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, token]);

  const cooldown = useCooldown();
  const resend = useMutation({
    mutationFn: () => resendInvite(token),
    // Sent or refused as too soon, the button waits out the minute either way.
    onSuccess: () => cooldown.start(60),
    onError: (error) => {
      const wait = retryAfterSec(error);
      if (wait) cooldown.start(wait);
    },
  });

  const { user, loading, signOut } = useAuth();
  const { refetch } = useOrg();

  const details = useQuery({
    queryKey: ['invite', token],
    queryFn: () => fetchInvite(token),
    enabled: Boolean(user),
    retry: false,
  });

  const go = useGo();
  const queryClient = useQueryClient();
  const accept = useMutation({
    mutationFn: () => acceptInvite(token),
    onSuccess: (joined) => {
      track('Invite Accepted', { role: joined.role });
      refetch();
      // The library lists this one as waiting until told otherwise.
      void queryClient.invalidateQueries({ queryKey: qk.myInvites() });
      // Straight into the library. The page that used to sit here said "you
      // are in" and offered a button going where pressing Join already meant
      // to go: a step whose only content was the absence of the next one.
      go('/library', { replace: true });
    },
  });

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
          <Spinner />
          <span className="text-body">Checking your session…</span>
        </div>
      </Shell>
    );
  }

  if (autoSignIn === 'working') {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
          <Spinner />
          <span className="text-body">Signing you in…</span>
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">You have been invited to a team on Slate</h1>
        {autoSignIn === 'failed' ? (
          <div className="mt-4">
            <Banner tone="caution" title="That sign-in link has already been used">
              Links from an invitation work once, so that forwarding one does not hand over the account. Ask for a
              fresh one and it will arrive at the invited address.
            </Banner>
            <div className="mt-3 flex items-center gap-3">
              <Button
                variant="primary"
                busy={resend.isPending}
                disabled={cooldown.secondsLeft > 0}
                onClick={() => resend.mutate()}
              >
                {cooldown.secondsLeft > 0
                  ? `${resend.isSuccess ? 'Sent' : 'Wait'} · ${cooldown.secondsLeft}s`
                  : 'Email me a fresh link'}
              </Button>
              {resend.isSuccess ? (
                <span className="text-caption text-go">Check {resend.data?.emailHint ?? 'your inbox'}.</span>
              ) : null}
            </div>
            {resend.isError && !retryAfterSec(resend.error) ? (
              <div className="mt-2">
                <ErrorText>{errorMessage(resend.error)}</ErrorText>
              </div>
            ) : null}
          </div>
        ) : null}
        <p className="mt-2 text-body text-chalk-dim">
          Sign in with the email address the invitation was sent to and we will show you the team
          and what joining it means. If you do not have an account yet, creating one takes a moment.
          Use that same address.
        </p>
        {/* Both carry the invitation with them. Without this the page sent
            people to sign up and asked them to find their way back here
            afterwards, which is the one moment they are least able to: a new
            account lands in an empty library, the invitation is a link in an
            email they have closed, and every sign that anything is unfinished
            is on a page they are no longer looking at. They believe they
            accepted, the team sees them as still invited, and the seat held
            for them goes unused. */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <AppLink to={returnHere('/login')} className="sm:flex-1">
            <Button variant="primary" size="lg" className="w-full">
              Sign in
            </Button>
          </AppLink>
          <AppLink to={returnHere('/signup')} className="sm:flex-1">
            <Button size="lg" className="w-full">
              Create an account
            </Button>
          </AppLink>
        </div>
        <p className="mt-4 text-caption text-chalk-faint">
          You will come straight back here to finish joining.
        </p>
      </Shell>
    );
  }


  // The invitation itself is dead: used, revoked, expired, or never existed.
  if (details.isError) {
    return (
      <Shell>
        <Banner tone="danger" title={failureTitle(details.error)}>
          {errorMessage(details.error)}
        </Banner>
        <AppLink to="/library" className="mt-5 block">
          <Button variant="primary" size="lg" className="w-full">
            Go to Slate
          </Button>
        </AppLink>
      </Shell>
    );
  }

  if (!details.data) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
          <Spinner />
          <span className="text-body">Reading the invitation…</span>
        </div>
      </Shell>
    );
  }

  const invite = details.data;
  const blocked = !invite.canAccept;

  return (
    <Shell branding={invite.branding}>
      <div className="flex items-center gap-2 text-chalk-faint">
        <UsersIcon size={18} />
        <span className="text-caption uppercase tracking-[0.08em]">Invitation</span>
      </div>
      <h1 className="mt-2 text-h3 text-chalk">You are about to join {invite.org.name}</h1>
      <p className="mt-2 text-body text-chalk-dim">
        {invite.invitedBy ? (
          <>
            <span className="text-chalk">{invite.invitedBy}</span> invited you as{' '}
          </>
        ) : (
          'You were invited as '
        )}
        {roleLabel(invite.role).toLowerCase()}. {roleDescription(invite.role)}
      </p>

      <p className="mt-4 text-caption uppercase tracking-[0.08em] text-chalk-faint">What changes</p>
      <ul className="mt-2 grid gap-2 text-body text-chalk-dim">
        <li className="flex gap-2">
          <span className="text-chalk-faint">·</span>
          <span>
            Everything you record from now on lands in {invite.org.name}'s library, and its admins can
            watch it. What you recorded before stays yours alone.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-chalk-faint">·</span>
          <span>
            {invite.seat === 'paid' ? `${invite.org.name} pays for your seat.` : 'You are on a free seat.'} Its admins
            can change your role or remove you from the team.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-chalk-faint">·</span>
          <span>You are on one team at a time. If you leave, you keep your recordings, and so does the team.</span>
        </li>
      </ul>

      {blocked ? (
        <div className="mt-4">
          <Blocked details={invite} onSignOut={() => void signOut()} />
        </div>
      ) : (
        <p className="mt-4 text-caption text-chalk-faint">
          Signed in as <span className="text-chalk-dim">{user.email}</span>.
        </p>
      )}

      {accept.isError ? (
        <div className="mt-4">
          <Banner tone="danger" title={failureTitle(accept.error)}>
            {errorMessage(accept.error)}
          </Banner>
        </div>
      ) : null}

      {blocked || (accept.isError && !recoverable(accept.error)) ? (
        <AppLink to="/library" className="mt-5 block">
          <Button variant="primary" size="lg" className="w-full">
            Go to Slate
          </Button>
        </AppLink>
      ) : (
        <Button
          variant="primary"
          size="lg"
          // `brand-button` falls back to the chalk a primary button already
          // uses, so a team without a colour gets exactly what it had.
          className={cx('mt-5 w-full', invite.branding?.brandColor && 'brand-button')}
          busy={accept.isPending}
          onClick={() => accept.mutate()}
        >
          {accept.isError ? 'Try again' : `Join ${invite.org.name}`}
        </Button>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <AppLink to="/library" className="text-caption text-chalk-faint hover:text-chalk-dim">
          Not now
        </AppLink>
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-caption text-chalk-faint transition-colors duration-120 hover:text-chalk-dim"
        >
          Wrong account? Sign out
        </button>
      </div>
    </Shell>
  );
}
